// filepath: src/apps/policymaker/pages/IR/page/PIRHeader.jsx
import React, {useState} from 'react';
import {Button, Stack, Typography} from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import {useSession} from '../../../../../firebase/UserProvider';
import {useParams} from "react-router";

const _awpBase = process.env.REACT_APP_AWP_API_URL || 'https://awp.minsky.ai';
function _authH() { const t = localStorage.getItem('access_token'); return t ? {Authorization: `Bearer ${t}`, 'Content-Type': 'application/json'} : {'Content-Type': 'application/json'}; }

/**
 * Small presentational component that renders the PIR header and Create button.
 * Props:
 * - title: header text
 * - createLabel: button label
 * - onCreate: callback for Create PIR button click (optional)
 */
export default function PIRHeader({
                                      title = 'Priority Intelligence Requirements',
                                      createLabel = 'Create PIR',
                                  }) {
    const {user} = useSession() || {user: null};
    const {irId} = useParams();

    const [open, setOpen] = useState(false);
    const [mainQuestion, setMainQuestion] = useState('What proxy attacks or mobilization indicators suggest Iran will respond via regional militias within 30 days?');
    const [description, setDescription] = useState('Are Iraqi militias receiving new funding/logistical support?\n' +
        'Are Lebanese or Syrian groups preparing operations?');
    const [assignedTo, setAssignedTo] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const openDialog = () => setOpen(true);
    const closeDialog = () => {
        if (saving) return;
        setOpen(false);
        setMainQuestion('');
        setDescription('');
        setAssignedTo('');
        setError(null);
    };

    const handleCreate = async () => {

        if (!mainQuestion) {
            setError('Title is required');
            return;
        }

        setSaving(true);
        setError(null);

        try {
            // Compute next PIR id by scanning existing PIR documents
            const listRes = await fetch(`${_awpBase}/api/intelligence-requests?type=PIR&limit=1000`, {headers: _authH()});
            const existingPirs = listRes.ok ? await listRes.json() : [];
            let max = 0;
            (Array.isArray(existingPirs) ? existingPirs : []).forEach(d => {
                const m = (d.id || '').match(/^PIR-(\d+)$/);
                if (m) { const n = parseInt(m[1], 10); if (!Number.isNaN(n)) max = Math.max(max, n); }
            });
            const newId = `PIR-${max + 1}`;

            const payload = {
                id: newId,
                type: 'PIR',
                pirId: newId,
                mainQuestion,
                description,
                createdBy: user?.uid || null,
                createdAt: new Date().toISOString(),
                assignedTo: assignedTo || null,
                parent_ir: irId || null,
            };

            await fetch(`${_awpBase}/api/intelligence-requests`, {method: 'POST', headers: _authH(), body: JSON.stringify(payload)});

            if (irId) {
                const irRes = await fetch(`${_awpBase}/api/intelligence-requests/${encodeURIComponent(irId)}`, {headers: _authH()});
                if (irRes.ok) {
                    const irDoc = await irRes.json();
                    const pir = Array.isArray(irDoc.pir) ? [...irDoc.pir, newId] : [newId];
                    await fetch(`${_awpBase}/api/intelligence-requests/${encodeURIComponent(irId)}`, {
                        method: 'PATCH', headers: _authH(), body: JSON.stringify({pir}),
                    });
                }
            }
            setSaving(false);
            closeDialog();
        } catch (e) {
            setError(String(e?.message || e));
            setSaving(false);
        }
    };

    return (
        <>
            <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'space-between'}>
                <Typography variant={'h6'}>{title}</Typography>
                <Button onClick={openDialog}>{createLabel}</Button>
            </Stack>

            <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>Create Priority Intelligence Requirement</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} mt={1}>
                        <TextField
                            autoFocus={true}
                            label="Main Question" value={mainQuestion} onChange={e => setMainQuestion(e.target.value)}
                            fullWidth
                            required/>
                        <TextField
                            label="Details"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            fullWidth multiline minRows={3}/>
                        {error && <Typography color={'error'}>{error}</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDialog} disabled={saving}>Cancel</Button>
                    <Button onClick={handleCreate} disabled={saving}
                            variant="contained">{saving ? 'Saving…' : 'Create'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
